import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, HStack, IconButton, Tooltip } from '@bonusly/design-system';

import { DOODLE_COLORS, DOODLE_SIZES, DoodleTool } from './use-doodle';

interface ToolButtonProps {
	label: string;
	isActive: boolean;
	onClick: () => void;
	children: React.ReactNode;
}

const ToolButton: FC<ToolButtonProps> = ({ label, isActive, onClick, children }) => (
	<Tooltip label={label}>
		<Box
			as="button"
			aria-label={label}
			aria-pressed={isActive}
			display="flex"
			alignItems="center"
			justifyContent="center"
			w="32px"
			h="32px"
			borderRadius="md"
			bg={isActive ? 'primary.bgAlpha' : 'transparent'}
			color={isActive ? 'primary.fg' : 'text.soft'}
			border="1.5px solid"
			borderColor={isActive ? 'primary.fg' : 'transparent'}
			cursor="pointer"
			transition="all 0.15s"
			_hover={{ bg: isActive ? 'primary.bgAlpha' : 'secondary.bgAlpha', color: isActive ? 'primary.fg' : 'text.default' }}
			onClick={onClick}
		>
			{children}
		</Box>
	</Tooltip>
);

interface DoodleToolbarProps {
	tool: DoodleTool;
	color: string;
	size: number;
	canUndo: boolean;
	canRedo: boolean;
	onToolChange: (tool: DoodleTool) => void;
	onColorChange: (color: string) => void;
	onSizeChange: (size: number) => void;
	onUndo: () => void;
	onRedo: () => void;
	onClear: () => void;
}

const BrushIcon: FC = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="20"
		height="20"
		fill="none"
		stroke="currentColor"
		strokeWidth="19"
		strokeLinecap="round"
		strokeLinejoin="round"
		viewBox="0 0 256 256"
	>
		<path d="M16,216H92a52,52,0,1,0-52-52C40,200,16,216,16,216Z" />
		<path d="M112.41,116.16C131.6,90.29,179.46,32,224,32c0,44.54-58.29,92.4-84.16,111.59" />
		<path d="M133,90.64a84.39,84.39,0,0,1,32.41,32.41" />
	</svg>
);

const FillIcon: FC = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="20"
		height="20"
		fill="none"
		stroke="currentColor"
		strokeWidth="19"
		strokeLinecap="round"
		strokeLinejoin="round"
		viewBox="0 0 256 256"
	>
		<path d="M208,139.48l-79.83,79.83a16,16,0,0,1-22.63,0L20.69,134.46a16,16,0,0,1,0-22.63L116.52,16,232,131.48Z" />
		<circle cx="124" cy="116" r="20" />
		<line x1="109.86" y1="101.86" x2="39.99" y2="32" />
		<path d="M248,208a16,16,0,0,1-32,0c0-16,16-40,16-40S248,192,248,208Z" />
	</svg>
);

const EraserIcon: FC = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="20"
		height="20"
		fill="none"
		stroke="currentColor"
		strokeWidth="19"
		strokeLinecap="round"
		strokeLinejoin="round"
		viewBox="0 0 256 256"
	>
		<line x1="96" y1="104" x2="160" y2="168" />
		<path d="M112,216,219.31,108.69a16,16,0,0,0,0-22.63L177.94,44.69a16,16,0,0,0-22.63,0L36.69,163.31a16,16,0,0,0,0,22.63L66.75,216H216" />
	</svg>
);

const UndoIcon: FC = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
		<path d="M3 7v6h6" />
		<path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
	</svg>
);

const RedoIcon: FC = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
		<path d="M21 7v6h-6" />
		<path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" />
	</svg>
);

const TrashIcon: FC = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
		<polyline points="3 6 5 6 21 6" />
		<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
	</svg>
);

export const DoodleToolbar: FC<DoodleToolbarProps> = ({
	tool,
	color,
	size,
	canUndo,
	canRedo,
	onToolChange,
	onColorChange,
	onSizeChange,
	onUndo,
	onRedo,
	onClear,
}) => {
	const { t } = useTranslation('admin_bizy');

	const showColors = tool !== 'eraser';
	const showSizes = tool === 'brush' || tool === 'eraser';

	return (
		<Flex direction={{ base: 'column', sm: 'row' }} gap={3} align="center" justify="space-between" width="100%">
			<HStack spacing={2} flexWrap="wrap" justify="center">
				<ToolButton label={t('doodle.brush')} isActive={tool === 'brush'} onClick={() => onToolChange('brush')}>
					<BrushIcon />
				</ToolButton>

				<ToolButton label={t('doodle.fill')} isActive={tool === 'fill'} onClick={() => onToolChange('fill')}>
					<FillIcon />
				</ToolButton>

				<ToolButton label={t('doodle.eraser')} isActive={tool === 'eraser'} onClick={() => onToolChange('eraser')}>
					<EraserIcon />
				</ToolButton>

				{showColors && (
					<>
						<Box w="1px" h="24px" bg="border.default" mx={1} display={{ base: 'none', sm: 'block' }} />

						<HStack spacing={1}>
							{DOODLE_COLORS.map((c) => (
								<Tooltip key={c.hex} label={t(`doodle.colors.${c.name}`)}>
									<Box
										as="button"
										aria-label={t(`doodle.colors.${c.name}`)}
										w="24px"
										h="24px"
										borderRadius="full"
										bg={c.hex}
										border="2px solid"
										borderColor={color === c.hex ? 'primary.fg' : 'transparent'}
										cursor="pointer"
										transition="all 0.15s"
										_hover={{ transform: 'scale(1.15)' }}
										onClick={() => onColorChange(c.hex)}
									/>
								</Tooltip>
							))}
						</HStack>
					</>
				)}

				{showSizes && (
					<>
						<Box w="1px" h="24px" bg="border.default" mx={1} display={{ base: 'none', sm: 'block' }} />

						<HStack spacing={1}>
							{DOODLE_SIZES.map((s) => (
								<Tooltip key={s.value} label={t(`doodle.sizes.${s.name}`)}>
									<Box
										as="button"
										aria-label={t(`doodle.sizes.${s.name}`)}
										display="flex"
										alignItems="center"
										justifyContent="center"
										w="32px"
										h="32px"
										borderRadius="md"
										bg={size === s.value ? 'primary.bgAlpha' : 'transparent'}
										border="1.5px solid"
										borderColor={size === s.value ? 'primary.fg' : 'transparent'}
										cursor="pointer"
										transition="all 0.15s"
										_hover={{ bg: size === s.value ? 'primary.bgAlpha' : 'secondary.bgAlpha' }}
										onClick={() => onSizeChange(s.value)}
									>
										<Box w={`${s.dot}px`} h={`${s.dot}px`} borderRadius="full" bg={tool === 'eraser' ? 'text.default' : color} />
									</Box>
								</Tooltip>
							))}
						</HStack>
					</>
				)}
			</HStack>

			<HStack spacing={1}>
				<Tooltip label={t('doodle.undo')}>
					<IconButton
						aria-label={t('doodle.undo')}
						icon={<UndoIcon />}
						size="sm"
						variant="ghost"
						colorScheme="gray"
						onClick={onUndo}
						isDisabled={!canUndo}
						borderRadius="md"
					/>
				</Tooltip>

				<Tooltip label={t('doodle.redo')}>
					<IconButton
						aria-label={t('doodle.redo')}
						icon={<RedoIcon />}
						size="sm"
						variant="ghost"
						colorScheme="gray"
						onClick={onRedo}
						isDisabled={!canRedo}
						borderRadius="md"
					/>
				</Tooltip>

				<Tooltip label={t('doodle.clear')}>
					<IconButton aria-label={t('doodle.clear')} icon={<TrashIcon />} size="sm" variant="ghost" colorScheme="gray" onClick={onClear} borderRadius="md" />
				</Tooltip>
			</HStack>
		</Flex>
	);
};
